import { ListingDraft, WithdrawalDraft } from '../domain/marketplaceTypes';
import { authService } from './auth';
import { MarketplaceSnapshot } from './marketplaceStorage';

type ApiSuccess<T> = {
  success: true;
  message?: string;
  data: T;
};

type ApiError = {
  success?: false;
  message?: string;
  error?: string;
};

function buildHeaders(): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = authService.getSessionToken();
  const businessId = authService.getBusinessId();

  if (token) headers.Authorization = `Bearer ${token}`;
  if (businessId) headers['X-Business-ID'] = businessId;

  return headers;
}

async function parseJson<T>(response: Response): Promise<T | null> {
  try {
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

function buildErrorMessage(raw: ApiError | null, fallback: string): string {
  return raw?.message || raw?.error || fallback;
}

async function fetchWithRetry(url: string, options: RequestInit): Promise<Response> {
  // Always build fresh headers to get the latest token
  options.headers = buildHeaders();
  let response = await fetch(url, options);

  if (response.status === 401 && window !== window.parent) {
    window.parent.postMessage({ type: 'BIZTADA_REQUIRE_AUTH_REFRESH' }, '*');
    try {
      const newToken = await authService.waitForNewToken(10000);
      if (newToken) {
        options.headers = buildHeaders(); // rebuild with new token
        response = await fetch(url, options);
      }
    } catch (e) {
      console.error('Failed to wait for new token from shell', e);
    }
  }
  return response;
}

export async function fetchMarketplaceState(): Promise<MarketplaceSnapshot> {
  const businessId = authService.getBusinessId();
  const search = businessId ? `?businessId=${encodeURIComponent(businessId)}` : '';
  const response = await fetchWithRetry(`/api/v1/marketplace/state${search}`, {
    method: 'GET',
  });
  const body = await parseJson<ApiSuccess<MarketplaceSnapshot> | ApiError>(response);

  if (!response.ok || !body || !('success' in body) || !body.success) {
    throw new Error(buildErrorMessage(body as ApiError | null, 'Không thể tải dữ liệu Marketplace từ server.'));
  }

  return body.data;
}

export async function createMarketplaceListing(
  draft: ListingDraft,
  actionKey: string,
): Promise<{ message: string; state: MarketplaceSnapshot }> {
  const response = await fetchWithRetry('/api/v1/marketplace/listings', {
    method: 'POST',
    body: JSON.stringify({
      businessId: authService.getBusinessId() || undefined,
      actionKey,
      draft,
    }),
  });

  const body = await parseJson<
    | ApiSuccess<{ listing: unknown; state: MarketplaceSnapshot }>
    | ApiError
  >(response);

  if (!response.ok || !body || !('success' in body) || !body.success) {
    throw new Error(buildErrorMessage(body as ApiError | null, 'Không thể tạo sản phẩm Marketplace.'));
  }

  return {
    message: body.message || 'Đăng sản phẩm thành công.',
    state: body.data.state,
  };
}

export async function requestMarketplaceWithdrawal(
  draft: WithdrawalDraft,
  actionKey: string,
): Promise<{ message: string; state: MarketplaceSnapshot }> {
  const response = await fetchWithRetry('/api/v1/marketplace/withdrawals', {
    method: 'POST',
    body: JSON.stringify({
      businessId: authService.getBusinessId() || undefined,
      actionKey,
      draft,
    }),
  });

  const body = await parseJson<
    | ApiSuccess<{ withdrawal: unknown; state: MarketplaceSnapshot }>
    | ApiError
  >(response);

  if (!response.ok || !body || !('success' in body) || !body.success) {
    throw new Error(buildErrorMessage(body as ApiError | null, 'Không thể tạo yêu cầu rút tiền.'));
  }

  return {
    message: body.message || 'Tạo yêu cầu rút tiền thành công.',
    state: body.data.state,
  };
}

export async function purchaseMarketplaceListing(
  listingId: string,
): Promise<{ message: string; tradeId?: string }> {
  const response = await fetchWithRetry('/api/v1/marketplace/listings/purchase', {
    method: 'POST',
    body: JSON.stringify({ listingId }),
  });

  const body = await parseJson<ApiSuccess<{ tradeId: string }> | ApiError>(response);

  if (!response.ok || !body || !('success' in body) || !body.success) {
    throw new Error(buildErrorMessage(body as ApiError | null, 'Không thể mua sản phẩm.'));
  }

  return {
    message: body.message || 'Giao dịch thành công, tiền đã được chuyển vào Escrow.',
    tradeId: body.data?.tradeId,
  };
}

export async function releaseTrade(
  tradeId: string,
): Promise<{ message: string }> {
  const response = await fetchWithRetry('/api/v1/marketplace/trades/release', {
    method: 'POST',
    body: JSON.stringify({ tradeId }),
  });

  const body = await parseJson<ApiSuccess<unknown> | ApiError>(response);

  if (!response.ok || !body || !('success' in body) || !body.success) {
    throw new Error(buildErrorMessage(body as ApiError | null, 'Không thể giải phóng tiền.'));
  }

  return {
    message: body.message || 'Đã giải phóng tiền cho người bán.',
  };
}

export async function disputeTrade(
  tradeId: string,
  reason: string,
): Promise<{ message: string }> {
  const response = await fetchWithRetry('/api/v1/marketplace/trades/dispute', {
    method: 'POST',
    body: JSON.stringify({ tradeId, reason }),
  });

  const body = await parseJson<ApiSuccess<unknown> | ApiError>(response);

  if (!response.ok || !body || !('success' in body) || !body.success) {
    throw new Error(buildErrorMessage(body as ApiError | null, 'Không thể báo cáo tranh chấp.'));
  }

  return {
    message: body.message || 'Đã báo cáo tranh chấp thành công.',
  };
}
